import { useCallback, useEffect, useState } from 'react';
import {
  Box,
  CircularProgress,
  Stack,
  TextField,
  Typography,
  Alert,
  Button,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import api from '@/services/api';
import UserAvatar from '@/components/UserAvatar';
import type { MainAppUser } from '@/types';

const ROLE_LABELS: Record<string, string> = {
  referee: '裁判',
  admin: '管理員',
  developer: '開發者',
};

export function formatMainAppUser(u: MainAppUser): string {
  return `${u.reference_no} — ${u.full_name || ROLE_LABELS[u.role] || u.role}`;
}

function sourceLabel(source?: string, cached?: boolean): string {
  if (cached) return '本地快取（主系統暫時無法連線）';
  if (source === 'local') return '本地 hkhbref 資料庫';
  return 'hkhbref.com 主系統';
}

interface RefereeSearchProps {
  /** Hide users already in this set */
  excludeIds?: number[];
  /** Hide admin/developer (group member pickers) */
  refereesOnly?: boolean;
  /** Immediate add (e.g. expand group → POST member) */
  onAdd?: (user: MainAppUser) => void;
  addLabel?: string;
  /** Controlled multi-select (create / edit group) */
  selected?: MainAppUser[];
  onSelectedChange?: (users: MainAppUser[]) => void;
  /** Single-select mode (share dialog) */
  single?: boolean;
}

/** Unified referee search — one field, one list */
export function RefereeMemberSearch({
  excludeIds = [],
  refereesOnly = false,
  onAdd,
  addLabel = '加入',
  selected = [],
  onSelectedChange,
  single = false,
}: RefereeSearchProps) {
  const [q, setQ] = useState('');
  const [users, setUsers] = useState<MainAppUser[]>([]);
  const [source, setSource] = useState<string>('local');
  const [cached, setCached] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const exclude = new Set([...excludeIds, ...(single && selected.length ? [selected[0].id] : [])]);
  const selectedIds = new Set(selected.map((u) => u.id));
  const visibleUsers = refereesOnly ? users.filter((u) => u.role === 'referee') : users;

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    api
      .get<{ users: MainAppUser[]; count: number; source?: string; error?: string; cached?: boolean }>('/users/referees', {
        params: { q, limit: 150 },
      })
      .then((r) => {
        setUsers(r.data.users);
        setSource(r.data.source || 'local');
        setCached(!!r.data.cached);
        if (r.data.error) setError(r.data.error);
        else setError(null);
      })
      .catch((err) => {
        setUsers(err.response?.data?.users?.length ? err.response.data.users : []);
        const msg =
          err.response?.data?.error ||
          err.response?.data?.message ||
          '無法讀取裁判名單。請執行 npm run sync:local，確認 hkhbref 後端與 MySQL 已啟動，並重啟 data worker';
        setError(msg);
      })
      .finally(() => setLoading(false));
  }, [q]);

  useEffect(() => {
    const t = setTimeout(load, q ? 300 : 0);
    return () => clearTimeout(t);
  }, [load, q]);

  const handlePick = (user: MainAppUser) => {
    if (onAdd) {
      onAdd(user);
      return;
    }
    if (!onSelectedChange) return;
    if (single) {
      onSelectedChange([user]);
      return;
    }
    if (selectedIds.has(user.id)) return;
    onSelectedChange([...selected, user]);
  };

  return (
    <Stack spacing={1.5}>
      <TextField
        size="small"
        label="搜尋裁判"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="編號、姓名、電郵…"
        fullWidth
      />
      {error && <Alert severity={users.length > 0 ? 'info' : 'warning'}>{error}</Alert>}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      ) : visibleUsers.length === 0 && !error ? (
        <Typography color="text.secondary" variant="body2">
          找不到符合的裁判
        </Typography>
      ) : (
        <Box sx={{ maxHeight: 280, overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          {visibleUsers.map((u) => {
            const already = exclude.has(u.id) || selectedIds.has(u.id);
            return (
              <Stack
                key={u.id}
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                spacing={1.25}
                sx={{
                  px: 1.5,
                  py: 0.75,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  opacity: already ? 0.55 : 1,
                  '&:last-child': { borderBottom: 0 },
                }}
              >
                <UserAvatar
                  photoUrl={u.photo_url}
                  photoPath={u.photo_path}
                  referenceNo={u.reference_no}
                  fullName={u.full_name}
                  sx={{ width: 36, height: 36, fontSize: '0.9rem', flexShrink: 0 }}
                />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="body2" fontWeight={600} noWrap>
                    {u.reference_no}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {u.full_name || '—'} · {ROLE_LABELS[u.role] || u.role}
                    {u.email ? ` · ${u.email}` : ''}
                  </Typography>
                </Box>
                <Button
                  size="small"
                  variant={already ? 'text' : 'outlined'}
                  color="secondary"
                  disabled={already}
                  startIcon={already ? undefined : <AddIcon />}
                  onClick={() => handlePick(u)}
                  sx={{ ml: 1, flexShrink: 0 }}
                >
                  {already ? '已加入' : addLabel}
                </Button>
              </Stack>
            );
          })}
        </Box>
      )}
      {visibleUsers.length > 0 && (
        <Typography variant="caption" color="text.secondary">
          資料來源：{sourceLabel(source, cached)} · 共 {visibleUsers.length} 人
        </Typography>
      )}
    </Stack>
  );
}

/** @deprecated Use RefereeMemberSearch */
export function MainAppUserPicker({
  value,
  onChange,
  label: _label,
  helperText: _helperText,
}: {
  value: MainAppUser[];
  onChange: (users: MainAppUser[]) => void;
  label?: string;
  helperText?: string;
}) {
  return (
    <RefereeMemberSearch
      selected={value}
      onSelectedChange={onChange}
      excludeIds={value.map((u) => u.id)}
    />
  );
}

/** @deprecated Use RefereeMemberSearch */
export function MainAppUserBrowse(props: {
  onAdd?: (user: MainAppUser) => void;
  excludeIds?: number[];
  addLabel?: string;
}) {
  return <RefereeMemberSearch {...props} />;
}
